import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import * as TeamsState from '../../store/TeamsState';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { customersActionCreators } from '../../store/customers';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Input, InputOnChangeData, Link, Option, Spinner, Switch, SwitchOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { DisplayCountryList, IsValidCountryCode, IsVIESSupport } from '../../interfaces/Countries';
import { SupportedDocument } from '../../API/PeppolDirectory';
import { NeomyticConsultantID } from '../../interfaces/Consultant';
import { useConsultants } from '../Tools/ObjectLoader';

export interface VATInfo {
    name?: string | null;
    address?: string | null;
    isValid?: boolean | null;
    originalVatNumber?: string | null;
    vatNumber?: string | null;
    userError?: string | null;
}
export const retrieveVATInfo = async (token: string, countryCode: string, vatNUmber: string): Promise<VATInfo | null> => {
    if (!countryCode || !vatNUmber || !token) return null;

    return fetch(`api/VATValidator/` + countryCode + "/" + vatNUmber, {
        method: "get",
        headers: {
            "Content-Type": "application/json",
            authorization: "bearer " + token,
        },
        mode: "cors",
        cache: "default",
    })
        .then(response => {
            if (response.status === 403) {
                return null;
            }
            return response.json() as Promise<VATInfo>;
        })
        .then(data => {
            console.log("vat data", data);
            return data;
        });
};

export interface PeppolParticipant_ParticipantID {
    scheme?: string | null;
    value?: string | null;
}
export interface PeppolParticipant_Name {
    name?: string | null;
    language?: string | null;
}
export interface PeppolParticipant_Entitie {
    name?: PeppolParticipant_Name[] | null;
    countryCode?: string | null;
    geoInfo?: string | null;
    regDate?: string | null;
}
export interface PeppolParticipant {
    participantID?: PeppolParticipant_ParticipantID | null;
    entities?: PeppolParticipant_Entitie[] | null;
}
export interface PeppolParticipants {
    matches?: PeppolParticipant[] | null;
}
const retrievePeppolParticipantsList = async (token: string, countryCode: string, vatNUmber: string): Promise<PeppolParticipants | null> => {
    if (!countryCode || !vatNUmber || !token) return null;

    return fetch(`api/PeppolDirectory/` + countryCode + "/" + onlyDigits(vatNUmber), {
        method: "get",
        headers: {
            "Content-Type": "application/json",
            authorization: "bearer " + token,
        },
        mode: "cors",
        cache: "default",
    })
        .then(response => {
            if (response.status === 403) {
                return null;
            }
            return response.json() as Promise<PeppolParticipants>;
        })
        .then(data => {
            console.log("vat data", data);
            return data;
        });
};

const onlyDigits = (value: string): string =>
    value.replace(/\D+/g, "");


export const CustomersEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    const customerEdit = useSelector<ApplicationState, Customer | null>((state: ApplicationState) => state.customersState.customerEdit);
    const { consultants } = useConsultants();
    const consultant = React.useMemo(() => {
        if (consultants && customerEdit && customerEdit.consultantid) {
            return consultants.find(c => c.consultantid && c.consultantid.toUpperCase() === customerEdit.consultantid.toUpperCase());
        }
        return undefined;
    }, [customerEdit, consultants]);

    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields (use a single partial customer state to reduce many useStates)
    const [customer, setCustomer] = React.useState<Partial<Customer>>(customerEdit ? { ...customerEdit } : {});
    React.useEffect(() => { setCustomer(customerEdit ? { ...customerEdit } : {}); }, [customerEdit]);
    // Simplified validation: compute field-level validity and overall submit validity in one place
    const [vatTestInProgress, setVatTestInProgress] = React.useState(false);
    const [vatInfo, setVatInfo] = React.useState<VATInfo | undefined>(undefined);
    const [peppolParticipantsTestInProgress, setPeppolParticipantsTestInProgress] = React.useState(false);
    const [peppolParticipants, setPeppolParticipants] = React.useState<PeppolParticipants | undefined>(undefined);

    const validation = React.useMemo(() => {
        const displayNameValid = !!(customer.displayName && customer.displayName.toString().trim().length > 0);
        const nameValid = !!(customer.name && customer.name.toString().trim().length > 0);
        const addressValid = !!(customer.address && customer.address.toString().trim().length > 0);
        const postalCodeValid = !!(customer.postalCode && customer.postalCode.toString().trim().length > 0);
        const cityValid = !!(customer.city && customer.city.toString().trim().length > 0);
        const countryValid = !!(customer.country && customer.country.toString().trim().length > 0);

        let canSave = displayNameValid && nameValid && addressValid && postalCodeValid && cityValid && countryValid;

        if (canSave && customerEdit) {
            const norm = (v: any) => (v === null || v === undefined) ? '' : v.toString().trim().toLowerCase();
            const changed = (
                norm(customer.displayName) !== norm(customerEdit.displayName)
                || norm(customer.name) !== norm(customerEdit.name)
                || norm(customer.vat) !== norm(customerEdit.vat)
                || norm(customer.address) !== norm(customerEdit.address)
                || norm(customer.postalCode) !== norm(customerEdit.postalCode)
                || norm(customer.city) !== norm(customerEdit.city)
                || norm(customer.country) !== norm(customerEdit.country)
                || (customer.defaultLanguage ?? '') !== (customerEdit.defaultLanguage ?? '')
                || (customer.paymentTerms ?? 30) !== (customerEdit.paymentTerms ?? 30)
                || (customer.vatexempt ?? false) !== (customerEdit.vatexempt ?? false)
                || (customer.isActif ?? true) !== (customerEdit.isActif ?? true)
                || norm(customer.peppolIdentifier) !== norm(customerEdit.peppolIdentifier)
                || (customer.countryCode ?? '').toString().toUpperCase() !== (customerEdit.countryCode ?? '').toString().toUpperCase()
            );
            canSave = canSave && changed;
        }

        return { displayNameValid, nameValid, addressValid, postalCodeValid, cityValid, countryValid, canSave };
    }, [customer, customerEdit, vatInfo]);

    // Whether VAT has been modified compared to the original customerEdit (for new customers, consider non-empty VAT as modified)
    const isVatModified = React.useMemo(() => {
        if (!customerEdit) return !!(customer.vat && customer.vat.length > 0);
        return (customer.vat ?? '') !== (customerEdit.vat ?? '');
    }, [customer.vat, customerEdit]);

    React.useEffect(() => {
        let cleanedVat = customer.vat as string | undefined;
        if (cleanedVat !== null && cleanedVat !== undefined) {
            cleanedVat = cleanedVat.replace(' ', '').replace('.', '').toUpperCase();
            if (customer.vat !== cleanedVat) {
                setCustomer(prev => ({ ...prev, vat: cleanedVat }));
            }
        }
        if ((customer.countryCode === null || customer.countryCode === undefined) && cleanedVat !== null && cleanedVat !== undefined && cleanedVat !== "" && cleanedVat.length >= 2) {
            const countryCodeToTest = cleanedVat.substring(0, 2);
            const countryCodeToTestRes = IsValidCountryCode(countryCodeToTest);
            if (countryCodeToTestRes) {
                setCustomer(prev => ({ ...prev, countryCode: cleanedVat.substring(0, 2).toUpperCase() }));
            }
        }
    }, [customer.countryCode, customer.vat]);

    const submit = () => {
        const c: Customer = {
            customerid: customer.customerid ?? null,
            displayName: customer.displayName ?? "",
            name: customer.name ?? "",
            vat: customer.vat ?? "",
            vatcode: 21,
            vatexempt: !!customer.vatexempt,
            address: customer.address ?? "",
            address2: customer.address2 ?? null,
            address3: customer.address3 ?? null,
            postalCode: customer.postalCode ?? "",
            city: customer.city ?? "",
            country: customer.country ?? "",
            contacts: null,
            defaultLanguage: customer.defaultLanguage ?? null,
            paymentTerms: customer.paymentTerms ?? 30,
            isActif: customer.isActif ?? true,
            peppolIdentifier: customer.peppolIdentifier ?? null,
            countryCode: customer.countryCode ?? null,
            consultantid: customer.consultantid ?? NeomyticConsultantID,
            sharedWithAllConsultants: false,
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                const toastId = (Math.random() + 1).toString(36).substring(7);
                dispatchToast(
                    <Toast appearance="inverted">
                        <ToastTitle>
                            {"Saving customer " + c.displayName + "..."}
                        </ToastTitle>
                    </Toast>,
                    { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                );
                if (token !== null) {
                    fetch("api/Customers/" + (customerEdit && customerEdit.customerid !== null ? customerEdit.customerid : ""), {
                        method: customerEdit && customerEdit.customerid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                {c.displayName ? ("Customer " + c.displayName + " saved") : "Customer saved"}
                                            </ToastTitle>
                                        </Toast>),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                return response.json() as Promise<Customer>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                {c.displayName ? ("Customer " + c.displayName + " save failed") : "Customer save failed"}
                                            </ToastTitle>
                                        </Toast>),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        })
                        .then(data => {
                            if (data) dispatch(customersActionCreators.patchCustomerEdition(data));
                            dispatch(customersActionCreators.requestCustomerEdition(null));
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>{
                    customerEdit && customerEdit.customerid !== null
                        ?
                        t('EditCustomer',
                            {
                                customer: customerEdit.displayName,
                                consultant: consultant ? (consultant.companyName ?? consultant.displayName) : "?",
                                interpolation: { escapeValue: false }
                            })
                        :
                        t('CreateNewCustomer',
                            {
                                consultant: consultant ? (consultant.companyName ?? consultant.displayName) : "?", interpolation: { escapeValue: false }
                            })
                }</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(customersActionCreators.requestCustomerEdition(null));
                        }}
                        className={useStackClassName()}
                        autoComplete="off"
                    >
                        <div className={styles.ContentBoxBrandBackground}>
                            <Field label={t('DisplayName')} required>
                                <Input
                                    autoComplete="off"
                                    className={styles.fluid}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, displayName: data.value }))}
                                    value={customer.displayName ?? ""}
                                    maxLength={100}
                                />
                                {/*    error={!isDisplayNameValid}*/}
                            </Field>
                            <Field label={t('Name')} required>
                                <Input
                                    autoComplete="off"
                                    className={styles.fluid}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, name: data.value }))}
                                    value={customer.name ?? undefined}
                                    maxLength={100}
                                />
                                {/*error={!isNameValid}*/}
                            </Field>
                        </div>
                        <div className={styles.ContentBoxBrandBackground}>
                            <div className={styles.onSingleLine}>
                                <div className={styles.field30}>
                                    <Field
                                        className={styles.fluid}
                                        label={t('CountryCode')}
                                    >
                                        <Dropdown
                                            className={styles.fluid}
                                            size="medium"
                                            value={customer.countryCode ? customer.countryCode.toUpperCase() : ""}
                                            onOptionSelect={(event, data) => {
                                                setCustomer(prev => ({ ...prev, countryCode: data.optionValue ?? "be" }));
                                            }}
                                        >
                                            {
                                                DisplayCountryList("be").map((i) =>
                                                    <Option text={i.displayName} value={i.alpha2}>
                                                        {i.displayName}
                                                    </Option>
                                                )
                                            }
                                        </Dropdown>
                                        {/*error={!isVatValid}*/}
                                    </Field>
                                </div>
                                <div className={styles.field40}>
                                    <Field
                                        label={t('VAT')}
                                        className={styles.fluid}
                                        validationMessage={
                                            vatInfo
                                                ?
                                                vatInfo.isValid
                                                    ?
                                                    "Valid"
                                                    :
                                                    vatInfo.userError === "INVALID"
                                                        ?
                                                        "error"
                                                        :
                                                        vatInfo.userError === "MS_MAX_CONCURRENT_REQ"
                                                            ?
                                                            t('ServerBusy')
                                                            :
                                                            "warning"
                                                :
                                                null
                                        }
                                        validationState={
                                            vatInfo
                                                ?
                                                vatInfo.isValid
                                                    ?
                                                    "success"
                                                    :
                                                    vatInfo.userError === "INVALID"
                                                        ?
                                                        "error"
                                                        :
                                                        "warning"
                                                :
                                                "none"
                                        }>
                                        <Input
                                            autoComplete="off"
                                            className={styles.fluid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, vat: data.value }))}
                                            value={customer.vat ? customer.vat : ""}
                                            maxLength={50}
                                            placeholder={'BE0123456789'}
                                        />
                                        {/*error={!isVatValid}*/}
                                    </Field>
                                </div>
                                <div className={styles.field30}>
                                    {vatTestInProgress
                                        ?
                                        <Spinner size="extra-tiny" />
                                        :
                                        <Button
                                            className={styles.fluid}
                                            onClick={() => {
                                                setVatTestInProgress(true);
                                                const cc = customer.countryCode;
                                                const v = customer.vat;
                                                TokenHelper.GetToken(instance, teamsState)
                                                    .then(token => {
                                                        if (cc && v) {
                                                            retrieveVATInfo(token, cc, v)
                                                                .then((r) => {
                                                                    if (r) {
                                                                        if (r.isValid && r.vatNumber) {
                                                                            const validatedVat = cc.toUpperCase() + r.vatNumber;
                                                                            setCustomer(prev => ({ ...prev, vat: validatedVat }));
                                                                            setVatInfo(r);
                                                                        }
                                                                        else {
                                                                            // store the response but do not mark it as 'validated'
                                                                            setVatInfo(r);
                                                                        }
                                                                    }
                                                                    setVatTestInProgress(false);
                                                                })
                                                                .catch(() => {
                                                                    setVatTestInProgress(false);
                                                                });
                                                        }
                                                        else {
                                                            setVatTestInProgress(false);
                                                        }
                                                    })
                                                    .catch(() => {
                                                        setVatTestInProgress(false);
                                                    });
                                            }}
                                            type="submit"
                                            appearance="primary"
                                            disabled={!isVatModified || !customer.countryCode || !customer.vat || vatTestInProgress || !IsVIESSupport(customer.countryCode as string) || (vatInfo && vatInfo.isValid === true)}
                                        >
                                            Test
                                        </Button>
                                    }

                                </div>
                            </div>
                            {vatInfo && vatInfo.isValid &&
                                <div>
                                    {t('Name') + " : " + vatInfo.name + " (" + vatInfo.address + ")"}
                                </div>
                            }
                            <Field label={t('PeppolParticipantId')} validationMessage={
                                peppolParticipants && peppolParticipants.matches && peppolParticipants.matches.length === 0
                                    ?
                                    t('NoMatchFound')
                                    :
                                    null
                            }
                                validationState={
                                    peppolParticipants && peppolParticipants.matches && peppolParticipants.matches.length === 0
                                        ?
                                        "error"
                                        :
                                        "none"
                                }>

                                <div className={styles.onSingleLine}>
                                    <div className={styles.field70}>
                                        <Input
                                            autoComplete="off"
                                            className={styles.fluid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, peppolIdentifier: data.value }))}
                                            value={customer.peppolIdentifier ? customer.peppolIdentifier : ""}
                                            maxLength={50}
                                        />
                                    </div>
                                    <div className={styles.field30}>
                                        {peppolParticipantsTestInProgress
                                            ?
                                            <Spinner size="extra-tiny" />
                                            :
                                            <Button
                                                className={styles.fluid}
                                                onClick={() => {
                                                    setPeppolParticipantsTestInProgress(true);
                                                    const cc = customer.countryCode;
                                                    const v = customer.vat;
                                                    const pid = customer.peppolIdentifier;
                                                    TokenHelper.GetToken(instance, teamsState)
                                                        .then(token => {
                                                            if (cc && v) {
                                                                retrievePeppolParticipantsList(token, cc, v)
                                                                    .then((r) => {
                                                                        if (r) {
                                                                            setPeppolParticipants(r);
                                                                        }
                                                                        setPeppolParticipantsTestInProgress(false);
                                                                    })
                                                                    .catch(() => {
                                                                        setPeppolParticipantsTestInProgress(false);
                                                                    });
                                                            }
                                                            else {
                                                                setPeppolParticipantsTestInProgress(false);
                                                            }
                                                            if (pid) {
                                                                SupportedDocument(token, pid, "Prod")
                                                                    .then((r) => {
                                                                        console.log(r);
                                                                    })
                                                                    .catch((e) => {
                                                                        console.log(e);
                                                                    });
                                                            }
                                                        })
                                                        .catch(() => {
                                                            setPeppolParticipantsTestInProgress(false);
                                                        });
                                                }}
                                                type="submit"
                                                appearance="primary"
                                                disabled={!customer.countryCode || !customer.vat || peppolParticipantsTestInProgress}
                                            >
                                                {t("Lookup")}
                                            </Button>
                                        }
                                    </div>
                                </div>
                                {/*error={!isNameValid}*/}
                            </Field>
                            {peppolParticipants && peppolParticipants.matches && peppolParticipants.matches.length > 0 &&
                                peppolParticipants.matches.map((i) => {
                                    if (i.entities && i.entities) {
                                        return <div className={styles.onSingleLine}>
                                            {i.entities.map((e) => {
                                                const n = e?.name;
                                                if (n && n.length > 0) {
                                                    return <>{n[0].name},</>;
                                                }
                                                return <>unknown</>;
                                            })
                                            }
                                            {" " + i.participantID?.value}
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    if (i.participantID && i.participantID.value) setCustomer(prev => ({ ...prev, peppolIdentifier: i.participantID!.value }));
                                                }}
                                                type="submit"
                                                appearance="primary"
                                            >
                                                {t("Select")}
                                            </Button>

                                        </div>;
                                    }
                                    return null;
                                }
                                )
                            }
                        </div>
                        <div className={styles.ContentBoxBrandBackground}>
                            <Field label={t('Address')} required>
                                <Input
                                    autoComplete="off"
                                    className={styles.fluidWithZeroBottomMargin}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, address: data.value }))}
                                    value={customer.address ? customer.address : ""}
                                />
                                <Input
                                    autoComplete="off"
                                    className={styles.fluidWithZeroBottomMargin}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, address2: data.value }))}
                                    value={customer.address2 ? customer.address2 : ""}
                                />
                                <Input
                                    autoComplete="off"
                                    className={styles.fluid}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, address3: data.value }))}
                                    value={customer.address3 ? customer.address3 : ""}
                                />
                            </Field>
                            <div className={styles.onSingleLine}>
                                <div className={styles.field50}>
                                    <Field label={t('postalCode')} className={styles.fluid} required>
                                        <Input
                                            autoComplete="off"
                                            className={styles.fluid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, postalCode: data.value }))}
                                            value={customer.postalCode ? customer.postalCode : ""}
                                            maxLength={50}
                                        />
                                    </Field>
                                </div>
                                <div className={styles.field50}>
                                    <Field label={t('City')} className={styles.fluid} required>
                                        <Input
                                            autoComplete="off"
                                            className={styles.fluid}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, city: data.value }))}
                                            value={customer.city ? customer.city : ""}
                                            maxLength={50}
                                        />
                                    </Field>
                                </div>
                            </div>
                            <Field label={t('Country')} required>
                                <Input
                                    autoComplete="off"
                                    className={styles.fluid}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCustomer(prev => ({ ...prev, country: data.value }))}
                                    value={customer.country ? customer.country : ""}
                                    maxLength={50}
                                />
                            </Field>
                        </div>
                        <div className={styles.ContentBoxBrandBackground}>

                            <Field label={t('PaymentTerms')}>
                                <CurrencyInput
                                    setCurrency={(val: number) => setCustomer(prev => ({ ...prev, paymentTerms: val }))}
                                    currency={customer.paymentTerms ?? 30}
                                    digits={0}
                                    contentAfter={
                                        t('Days')
                                    }
                                />
                            </Field>
                            <Switch label={t('VATExempt')}
                                checked={customer.vatexempt ?? false}
                                onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setCustomer(prev => ({ ...prev, vatexempt: data.checked })); }} />
                        </div>
                        <Switch label={t('IsActif')}
                            checked={customer.isActif ?? true}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setCustomer(prev => ({ ...prev, isActif: data.checked })); }} />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(customersActionCreators.requestCustomerEdition(null))}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!validation.canSave}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};